import useSWR from 'swr';
import type { NskResponse, SwrReturnType } from '~/models/Api';
import type { Survey, SurveySetting } from '~/models/Survey';
import {
  deleteRequest,
  postRequest,
  putRequest
} from '~/utils/api/api_handler';

const paths = {
  index: '/vendors/surveys',
  show: '/vendors/surveys/:id',
  create: '/vendors/surveys',
  update: '/vendors/surveys/:id',
  delete: '/vendors/surveys/:id',
  activate: '/vendors/surveys/:id/activate',
  changeEnabled: '/vendors/surveys/:id/change_enabled',
  updateSeq: '/vendors/surveys/update_seq'
};

type TIndex = () => SwrReturnType<Survey[]>;
type TShow = (id: number | null) => SwrReturnType<Survey>;
type ReturnType = {
  index: TIndex;
  show: TShow;
};

export const useSurveySettings = (): ReturnType => {
  const index: TIndex = () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, mutate, isLoading, error } = useSWR<Survey[]>(paths.index);
    return {
      data: data ?? null,
      mutate,
      isLoading,
      error
    };
  };
  const show: TShow = (id: number | null) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, mutate, isLoading, error } = useSWR<Survey>(
      id === null ? null : paths.show.replace(':id', String(id))
    );
    return {
      data: data ?? null,
      mutate,
      isLoading,
      error
    };
  };
  return { index, show };
};

export const create = async (
  survey: SurveySetting
): Promise<NskResponse<void>> =>
  postRequest(paths.create, {
    survey
  });

export const update = async (
  survey: SurveySetting,
  surveyId: number
): Promise<NskResponse<void>> =>
  putRequest(paths.update.replace(':id', String(surveyId)), {
    survey
  });

export const destroy = async (surveyId: number): Promise<NskResponse<void>> =>
  deleteRequest(paths.delete.replace(':id', String(surveyId)));

export const activate = async (surveyId: number): Promise<NskResponse<void>> =>
  putRequest(paths.activate.replace(':id', String(surveyId)));

export const changeEnabled = async (
  surveyId: number,
  enabled: boolean
): Promise<NskResponse<void>> =>
  putRequest(paths.changeEnabled.replace(':id', String(surveyId)), { enabled });

export const updateSeq = async (
  surveys: Survey[]
): Promise<NskResponse<void>> =>
  postRequest(paths.updateSeq, {
    surveys
  });
